import React, { useState } from 'react';
import { Language } from '../types';
import { ChevronDown, HelpCircle, Phone, ArrowRight } from 'lucide-react';

export interface FaqItem {
  id: string;
  question: Record<Language, string>;
  answer: Record<Language, string>;
}

export const FAQ_DATA: FaqItem[] = [
  {
    id: 'faq-delai',
    question: {
      fr: "Quel est le délai d'intervention en cas d'urgence en Belgique ?",
      nl: "Wat is de gemiddelde aankomsttijd bij noodgevallen in België?",
      en: "What is your emergency response time across Belgium?"
    },
    answer: {
      fr: "Nos équipes mobiles interviennent en moyenne en 30 minutes chrono à Bruxelles, Sint-Pieters-Leeuw, Halle, Anvers, Liège, Gand et dans toute la Belgique. Pour les urgences critiques (fuite d'eau inondante, panne totale de chaudière en période de froid ou refoulement d'égout), un artisan est dépêché en priorité absolue.",
      nl: "Onze mobiele interventieteams arriveren gemiddeld binnen 30 minuten in Brussel, Sint-Pieters-Leeuw, Halle, Antwerpen, Luik, Gent en heel België. Bij zware noodgevallen (overstromend lek, defecte gasketel in de winter of ernstige rioolverstopping) krijgt uw oproep absolute voorrang.",
      en: "Our mobile teams arrive on average within 30 minutes across Brussels, Sint-Pieters-Leeuw, Halle, Antwerp, Liège, Ghent, and all over Belgium. Severe emergencies (major leaks, boiler failure in cold weather, sewage backup) receive top priority dispatch."
    }
  },
  {
    id: 'faq-attestation',
    question: {
      fr: "Délivrez-vous l'attestation officielle pour l'entretien de chaudière gaz ?",
      nl: "Ontvang ik een wettelijk attest na het onderhoud van mijn gasketel?",
      en: "Do you provide an official legal certificate for gas boiler maintenance?"
    },
    answer: {
      fr: "Oui, tous nos chauffagistes sont habilités et agréés (Bruxelles Environnement, Région Flamande & Région Wallonne). Après chaque entretien périodique réglementaire (prix fixe de 125 € HTVA), nous vous délivrons immédiatement l'attestation de conformité officielle exigée par votre assurance incendie et la législation belge.",
      nl: "Ja, al onze chauffagisten zijn wettelijk gecertificeerd en erkend (Leefmilieu Brussel, Vlaams & Waals Gewest). Na elk periodiek onderhoud (vaste prijs van 125 € ex. BTW) ontvangt u onmiddellijk het officiële reinigings- en verbrandingsattest voor uw brandverzekering.",
      en: "Yes, our heating engineers are officially licensed and certified across Brussels, Flanders, and Wallonia. Following every mandatory boiler service (fixed price 125 € ex. VAT), we provide the official certificate required by your home insurance and Belgian regulations."
    }
  },
  {
    id: 'faq-tarifs',
    question: {
      fr: "Quels sont vos tarifs pour un dépannage ou un débouchage ?",
      nl: "Wat zijn de tarieven voor een herstelling of ontstopping?",
      en: "What are your rates for plumbing repairs or drain unblocking?"
    },
    answer: {
      fr: "API HEAT s'engage sur une totale transparence tarifaire sans frais cachés. Nos tarifs démarrent dès 85 € TVAC selon la prestation. Un devis clair et précis vous est présenté et validé avant le début des travaux. L'entretien de chaudière est au prix transparent de 125 € HTVA et le dépannage chaudière à 205 € HTVA (main d'œuvre).",
      nl: "API HEAT hanteert 100% transparante tarieven zonder verborgen kosten. Onze tarieven beginnen vanaf 85 € incl. BTW naargelang de interventie. Vooraf geven we altijd een duidelijke prijsinschatting. Gasketelonderhoud bedraagt 125 € ex. BTW en depannage 205 € ex. BTW (werkuren).",
      en: "API HEAT practices 100% transparent pricing with no hidden charges. Standard interventions start from 85 € incl. VAT. A clear quote is always confirmed before starting work. Gas boiler maintenance is set at 125 € ex. VAT and boiler breakdown repair at 205 € ex. VAT (labor)."
    }
  },
  {
    id: 'faq-disponibilite',
    question: {
      fr: "Intervenez-vous la nuit, le week-end et les jours fériés ?",
      nl: "Zijn jullie 24/7 bereikbaar, ook 's nachts en in het weekend?",
      en: "Are you available 24/7, including nights, weekends, and public holidays?"
    },
    answer: {
      fr: "Oui, notre permanence technique d'urgence fonctionne 24h/24, 7j/7, dimanches et jours fériés compris. Vous pouvez nous joindre à toute heure au 0493 37 37 89 ou via notre ligne d'urgence WhatsApp.",
      nl: "Ja, onze technische nooddienst staat 24 uur per dag, 7 dagen op 7, 365 dagen per jaar paraat. U kan ons te allen tijde telefonisch bereiken op 0493 37 37 89 of via onze dringende WhatsApp-lijn.",
      en: "Yes, our technical dispatch operates 24 hours a day, 7 days a week, 365 days a year. You can reach our team anytime by calling 0493 37 37 89 or via our urgent WhatsApp line."
    }
  },
  {
    id: 'faq-zones',
    question: {
      fr: "Quelle est votre zone d'intervention autour de Sint-Pieters-Leeuw et Bruxelles ?",
      nl: "Wat is jullie werkgebied rond Sint-Pieters-Leeuw en Brussel?",
      en: "What is your primary coverage area around Sint-Pieters-Leeuw and Brussels?"
    },
    answer: {
      fr: "Notre siège technique est situé Bergensesteenweg 24/8 à 1600 Sint-Pieters-Leeuw. Nous garantissons une intervention ultra-rapide (15 à 25 minutes) sur Sint-Pieters-Leeuw, Halle, Beersel, Dilbeek, Anderlecht, Forest, Uccle et les 19 communes de Bruxelles, ainsi que des interventions régulières dans toute la Flandre, la Wallonie et le Brabant.",
      nl: "Onze hoofdvestiging bevindt zich aan de Bergensesteenweg 24/8 in 1600 Sint-Pieters-Leeuw. Wij garanderen een supersnelle interventie (15 tot 25 min) in Sint-Pieters-Leeuw, Halle, Beersel, Dilbeek, Anderlecht, Vorst, Ukkel en de 19 Brusselse gemeenten, evenals heel Vlaanderen en Wallonië.",
      en: "Our main base is located at Bergensesteenweg 24/8 in 1600 Sint-Pieters-Leeuw. We ensure fast arrival (15 to 25 minutes) in Sint-Pieters-Leeuw, Halle, Beersel, Dilbeek, Anderlecht, Forest, Uccle, all 19 Brussels municipalities, and nationwide across Belgium."
    }
  }
];

interface FaqSectionProps {
  currentLang: Language;
  onNavigateContact?: () => void;
}

export const FaqSection: React.FC<FaqSectionProps> = ({ currentLang, onNavigateContact }) => {
  const [openId, setOpenId] = useState<string | null>(FAQ_DATA[0].id);

  const toggle = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  const titles = {
    fr: {
      badge: "Questions Fréquentes • FAQ",
      title: "Tout savoir sur nos interventions d'urgence et nos tarifs",
      sub: "Réponses claires aux questions les plus posées par nos clients en Belgique (délais, agréations, devis, zones d'intervention).",
      moreQuestions: "Une autre question technique ?",
      contactUs: "Nous contacter 24/7"
    },
    nl: {
      badge: "Veelgestelde Vragen • FAQ",
      title: "Alles over onze dringende interventies en prijzen",
      sub: "Duidelijke antwoorden op de meest gestelde vragen van onze klanten in België (termijnen, keuringsattest, offertes, werkgebied).",
      moreQuestions: "Heeft u nog een andere vraag?",
      contactUs: "Contacteer ons 24/7"
    },
    en: {
      badge: "Frequently Asked Questions • FAQ",
      title: "Everything you need to know about our emergency call-outs and rates",
      sub: "Clear answers to the most frequent inquiries from customers across Belgium (response times, certification, quotes, coverage).",
      moreQuestions: "Have another technical question?",
      contactUs: "Contact us 24/7"
    }
  }[currentLang];

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
        <span className="text-xs font-bold uppercase tracking-wider text-orange-600 bg-orange-50 border border-orange-200/60 px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5">
          <HelpCircle className="w-4 h-4 text-orange-600" />
          <span>{titles.badge}</span>
        </span>
        <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
          {titles.title}
        </h2>
        <p className="text-sm sm:text-base text-slate-600">
          {titles.sub}
        </p>
      </div>

      {/* Accordion List */}
      <div className="space-y-3.5">
        {FAQ_DATA.map((item) => {
          const isOpen = openId === item.id;
          return (
            <div
              key={item.id}
              className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                isOpen
                  ? 'bg-white border-orange-300 shadow-md shadow-orange-500/5'
                  : 'bg-white/80 border-slate-200 hover:border-slate-300'
              }`}
            >
              <button
                type="button"
                onClick={() => toggle(item.id)}
                aria-expanded={isOpen}
                className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                <span className="font-bold text-slate-900 text-base sm:text-lg">
                  {item.question[currentLang]}
                </span>
                <span
                  className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 ${
                    isOpen ? 'bg-orange-500 text-white rotate-180' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <ChevronDown className="w-5 h-5" />
                </span>
              </button>

              {isOpen && (
                <div className="px-5 sm:px-6 pb-6 pt-1 text-slate-600 text-sm sm:text-base leading-relaxed border-t border-slate-100 animate-fadeIn">
                  {item.answer[currentLang]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom mini-banner */}
      <div className="mt-10 p-5 rounded-2xl bg-slate-900 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-center sm:text-left">
          <div className="w-10 h-10 rounded-xl bg-orange-600/30 flex items-center justify-center text-orange-400 shrink-0">
            <Phone className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm font-bold">{titles.moreQuestions}</p>
            <p className="text-xs text-slate-400">Centrale d'intervention : 0493 37 37 89 (24h/24 & 7j/7)</p>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <a
            href="tel:+32493373789"
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm shadow transition"
          >
            <span>0493 37 37 89</span>
          </a>
          {onNavigateContact && (
            <button
              onClick={onNavigateContact}
              className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm border border-slate-700 transition"
            >
              <span>{titles.contactUs}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
